/**
 * LivretQrShareModal.jsx — Modale de partage d'un livret pédagogique via QR code.
 *
 * Objectif pédagogique :
 *   Au lieu d'imprimer le livret sur papier, l'enseignant affiche un QR code que
 *   les élèves scannent avec leur smartphone. Le livret s'ouvre alors en mode
 *   tablette et les élèves peuvent cocher / répondre en direct pendant la séance.
 *
 * Fonctionnement :
 *   - Génère une URL vers le livret basée sur `window.location.origin` + `livretPath`.
 *   - Optionnel : deep-link vers une séquence précise via `?seq=xxx`.
 *   - Optionnel : force le mode Tablette via `?mode=tablette` (pratique sur mobile).
 *   - Bouton "Copier le lien", bouton "Ouvrir" et bouton "Imprimer le QR" (pour l'afficher).
 *
 * Générique — peut être réutilisé pour n'importe quel livret interactif.
 */
import React, { useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  X, Copy, Check, Printer, ExternalLink, Smartphone, QrCode,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * @param {boolean} open           - visibilité de la modale
 * @param {() => void} onClose     - callback de fermeture
 * @param {string} livretPath      - chemin du livret (ex: '/livrets/parcours-am')
 * @param {string} livretTitle     - titre pédagogique affiché en haut
 * @param {string} livretSubtitle  - sous-titre affiché sous le titre
 * @param {string} [sequenceId]    - id de séquence active pour deep-link (?seq=xxx)
 * @param {string} [sequenceLabel] - libellé lisible ex: "S2 · Sécurité"
 * @param {string} [accentColor]   - couleur d'accent (défaut rose Topolino)
 * @param {boolean} [tabletMode]   - ajoute ?mode=tablette (défaut true)
 */
export default function LivretQrShareModal({
  open,
  onClose,
  livretPath,
  livretTitle = 'Livret pédagogique',
  livretSubtitle = '',
  sequenceId = null,
  sequenceLabel = '',
  sequenceHint = '',
  accentColor = '#f43f5e',
  tabletMode = true,
  /** Ajoute automatiquement ?share=c2r pour que l'élève accède au livret
   *  sans créer de compte. Recommandé quand le prof partage en classe. */
  bypassAuth = true,
}) {
  const [copied, setCopied] = useState(false);
  const [includeSeq, setIncludeSeq] = useState(Boolean(sequenceId));
  const [includeTablet, setIncludeTablet] = useState(tabletMode);
  const [includeShare, setIncludeShare] = useState(bypassAuth);
  const qrWrapRef = useRef(null);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !livretPath) return '';
    const url = new URL(livretPath, window.location.origin);
    if (includeSeq && sequenceId) url.searchParams.set('seq', sequenceId);
    if (includeTablet) url.searchParams.set('mode', 'tablette');
    if (includeShare) url.searchParams.set('share', 'c2r');
    return url.toString();
  }, [livretPath, includeSeq, sequenceId, includeTablet, includeShare]);

  if (!open) return null;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Lien du livret copié — partage-le à tes élèves.');
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error('Copie impossible. Long-press sur le QR pour partager.');
    }
  };

  const printQr = () => {
    // Ouvre une petite fenêtre d'impression avec juste le QR + titre + URL
    if (typeof window === 'undefined') return;
    const w = window.open('', '_blank', 'width=520,height=720');
    if (!w) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression (popup bloqué).");
      return;
    }
    // Récupère le canvas via le ref parent (qrcode.react ne propage pas les data-attrs)
    const canvas = qrWrapRef.current?.querySelector('canvas');
    const dataUrl = canvas ? canvas.toDataURL('image/png') : '';
    w.document.write(`
      <!doctype html>
      <html lang="fr">
        <head>
          <meta charset="utf-8" />
          <title>QR ${livretTitle}</title>
          <style>
            body { font-family: Georgia, serif; text-align: center; padding: 32px 16px; color: #111; }
            h1 { font-size: 20px; margin: 0 0 4px; }
            .subtitle { color: #666; font-size: 13px; margin-bottom: 20px; }
            .qr { display: inline-block; padding: 12px; background: #fff; border: 2px solid ${accentColor}; border-radius: 12px; }
            .url { margin-top: 18px; font-family: monospace; font-size: 12px; word-break: break-all; color: #333; }
            .note { margin-top: 16px; font-size: 12px; color: #555; max-width: 380px; margin-left: auto; margin-right: auto; }
            @media print { body { padding: 12px; } }
          </style>
        </head>
        <body>
          <h1>${livretTitle}</h1>
          ${livretSubtitle ? `<div class="subtitle">${livretSubtitle}</div>` : ''}
          <div class="qr">${dataUrl ? `<img src="${dataUrl}" alt="QR" width="260" height="260" />` : '<em>QR indisponible</em>'}</div>
          <div class="url">${shareUrl}</div>
          <div class="note">Scanne ce QR avec ton smartphone pour ouvrir le livret et remplir tes réponses au fur et à mesure.</div>
          <script>window.onload = () => { setTimeout(() => window.print(), 200); };</script>
        </body>
      </html>
    `);
    w.document.close();
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="livret-qr-share-modal"
    >
      <div
        className="w-full max-w-md bg-[#0d0d0e] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: `0 24px 60px ${accentColor}22` }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="min-w-0">
            <div
              className="text-[10px] font-black uppercase tracking-[0.22em] flex items-center gap-1.5"
              style={{ color: accentColor }}
            >
              <QrCode className="w-3 h-3" /> Partager par QR code
            </div>
            <h2
              className="text-lg font-black text-white truncate"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              title={livretTitle}
            >
              {livretTitle}
            </h2>
            {livretSubtitle && (
              <div className="text-[11px] text-white/55 truncate">{livretSubtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-white/5 transition"
            aria-label="Fermer"
            data-testid="livret-qr-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Corps */}
        <div className="p-6 text-center">
          <p className="text-[12.5px] text-white/70 leading-relaxed mb-4">
            <Smartphone className="inline w-3.5 h-3.5 mr-1 -mt-0.5" style={{ color: accentColor }} />
            L'élève <strong className="text-white">scanne</strong> ce code avec son smartphone,
            le livret s'ouvre <strong className="text-white">directement</strong> et il peut
            cocher / répondre aux QCM au fur et à mesure de la séance — plus besoin d'imprimer.
          </p>

          {/* QR */}
          <div
            ref={qrWrapRef}
            className="mx-auto inline-block p-4 bg-white rounded-2xl"
            style={{ boxShadow: `0 0 0 3px ${accentColor}55, 0 12px 30px ${accentColor}33` }}
            data-testid="livret-qr-canvas-wrap"
          >
            <QRCodeCanvas
              value={shareUrl}
              size={220}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#0a0a0b"
            />
          </div>

          {/* URL courte */}
          <div className="mt-4 text-left">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1">
              Lien du livret
            </div>
            <div
              className="font-mono text-[11.5px] text-white/85 break-all rounded-lg bg-black/50 border border-white/10 px-3 py-2"
              data-testid="livret-qr-url"
            >
              {shareUrl}
            </div>
          </div>

          {/* Options */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] font-semibold">
            {sequenceId && (
              <label
                className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg border cursor-pointer transition"
                style={{
                  borderColor: includeSeq ? `${accentColor}80` : 'rgba(255,255,255,0.15)',
                  background: includeSeq ? `${accentColor}22` : 'rgba(255,255,255,0.02)',
                  color: includeSeq ? '#fff' : 'rgba(255,255,255,0.65)',
                }}
                data-testid="livret-qr-opt-seq"
              >
                <input
                  type="checkbox"
                  className="accent-rose-500"
                  checked={includeSeq}
                  onChange={(e) => setIncludeSeq(e.target.checked)}
                />
                Cibler {sequenceLabel || 'la séquence'}
              </label>
            )}
            <label
              className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg border cursor-pointer transition"
              style={{
                borderColor: includeTablet ? `${accentColor}80` : 'rgba(255,255,255,0.15)',
                background: includeTablet ? `${accentColor}22` : 'rgba(255,255,255,0.02)',
                color: includeTablet ? '#fff' : 'rgba(255,255,255,0.65)',
              }}
              data-testid="livret-qr-opt-tablet"
            >
              <input
                type="checkbox"
                className="accent-rose-500"
                checked={includeTablet}
                onChange={(e) => setIncludeTablet(e.target.checked)}
              />
              Mode tablette (mobile)
            </label>
            <label
              className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg border cursor-pointer transition"
              style={{
                borderColor: includeShare ? '#10b98180' : 'rgba(255,255,255,0.15)',
                background: includeShare ? 'rgba(16,185,129,0.14)' : 'rgba(255,255,255,0.02)',
                color: includeShare ? '#fff' : 'rgba(255,255,255,0.65)',
              }}
              title="Accès instantané, sans création de compte (recommandé en séance)"
              data-testid="livret-qr-opt-share"
            >
              <input
                type="checkbox"
                className="accent-emerald-500"
                checked={includeShare}
                onChange={(e) => setIncludeShare(e.target.checked)}
              />
              Accès sans compte
            </label>
          </div>

          {/* Info bandeau selon l'option choisie */}
          {includeShare ? (
            <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-left text-[11px] text-emerald-200/95 leading-relaxed">
              <strong className="text-emerald-300">Accès direct activé.</strong> L'élève scanne
              et arrive tout de suite dans le livret — pas de mot de passe, pas d'inscription.
              Il pourra créer un compte plus tard pour <em className="not-italic font-semibold">sauvegarder sa progression sur son téléphone</em>.
            </div>
          ) : (
            <div className="mt-3 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-left text-[11px] text-white/60 leading-relaxed">
              <strong className="text-white/85">Accès protégé.</strong> Seuls les élèves déjà
              inscrits pourront ouvrir le livret. Recommandé pour du travail à la maison.
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
            <button
              type="button"
              onClick={copyUrl}
              className="px-3 h-9 rounded-lg text-white text-[11.5px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition hover:brightness-110"
              style={{ background: accentColor }}
              data-testid="livret-qr-copy-btn"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copié !' : 'Copier le lien'}
            </button>
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 h-9 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[11.5px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition"
              data-testid="livret-qr-open-btn"
            >
              <ExternalLink size={13} /> Aperçu
            </a>
            <button
              type="button"
              onClick={printQr}
              className="px-3 h-9 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[11.5px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition"
              data-testid="livret-qr-print-btn"
            >
              <Printer size={13} /> Imprimer le QR
            </button>
          </div>

          <div className="mt-4 text-[10.5px] text-white/40 leading-relaxed">
            Astuce : projette ce QR en grand, chaque élève le scanne une seule fois puis retrouve
            sa progression sauvegardée à chaque connexion.
          </div>
        </div>
      </div>
    </div>
  );
}
